import {
  Component,
  ElementRef,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {Library} from "./library.model";
import {ObjectDetailService} from "../../object-detail.service";
import {BingMapService} from "../../../../bing-maps/service/bing-map.service";
import {AppConfigService} from "../../../../config/app-config.service";
import {HtmlUtilsService} from "../../../../html-utils/html-utils.service";
import {TranslateService} from '../../../../shared/translate.service';
import {Subscription} from "rxjs";

@Component({
  selector: 'app-library-list',
  templateUrl: './library-list.component.html',
  styleUrls: ['./library-list.component.scss']
})
export class LibraryListComponent implements OnInit, OnDestroy, OnChanges {

  private mobile: boolean;

  private mobileSubscription: Subscription;

  private mapCoords: any = {};

  @Output() libraryCount = new EventEmitter<number>();

  @Output() pageChange = new EventEmitter<number>();

  @Output() selectionLibsList = new EventEmitter<boolean>();

  @Output() selectionLibSync = new EventEmitter<boolean>();

  @Input() libraries: Library[] = [];

  @Input() showSelectionLibsList = true;

  @Input() shouldHideWrapper = false;

  @Input() shouldHideRefresh = false;

  @Input() shouldHideDigital = false;

  @Input() navigationOnRight = false;

  @Input() hideMaterializationCount = false;

  @Input() institutionsView = false;

  @Input() sortBy;

  @ViewChild('librariesWrap') private librariesWrap: ElementRef;

  pageCount: number;

  onlyDigital: boolean = false;

  refreshOnMapMove: boolean = false;

  librariesInView: Library[];

  librariesNumbers: Map<string, number> = new Map();

  pageNumber: number = 1;

  pageSize: number;

  showDistanceLabels: boolean = false;

  constructor(private objectDetailService: ObjectDetailService,
              private mapsService: BingMapService,
              private config: AppConfigService,
              public htmlUtils: HtmlUtilsService,
              public translateService: TranslateService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(this.libraries != null)
    {
      this.libraries.filter(l => !l.online).forEach(l => {
        l.url = this.getLibraryUrl(l);
      });
      this.updateNumbers(this.libraries);
      this.setPage(this.pageNumber);
    }
  }

  ngOnInit() {
    this.mobile = this.htmlUtils.mobileVersion;
    this.mobileSubscription = this.htmlUtils.mobileBehaviorSubject.subscribe(mobile => this.mobile = mobile);
    if(this.libraries != null)
      this.updateNumbers(this.libraries);
    this.pageSize = this.institutionsView ? this.config.institutionsLibrariesPerPage : this.config.librariesPerPage;
    this.setPage(1);
    this.mapsService.positionEmitter.subscribe(value => {
      if (value) {
        this.showDistanceLabels = true;
      }
    });
    this.mapsService.mapPositionEmitter.subscribe(pos => {
      if (pos) {
        this.mapCoords.lat1 = pos.northwest.latitude;
        this.mapCoords.lat2 = pos.southeast.latitude;
        this.mapCoords.lon1 = pos.northwest.longitude;
        this.mapCoords.lon2 = pos.southeast.longitude;
        if (this.refreshOnMapMove && !this.mobile) {
          this.updateVisibleLibrariesList(this.mapCoords.lat1, this.mapCoords.lat2, this.mapCoords.lon1, this.mapCoords.lon2);
        }
      }
    });
    this.objectDetailService.libraryClick.subscribe(() => {
      this.onlyDigital = true;
    })
  }

  ngOnDestroy(){
    if(this.mobileSubscription) {
      this.mobileSubscription.unsubscribe();
    }
  }

  refreshOnMapMoveChange() {
    if (!this.refreshOnMapMove) {
      this.setPage(1);
    }
    else if(!this.mobile){
      this.updateVisibleLibrariesList(this.mapCoords.lat1, this.mapCoords.lat2, this.mapCoords.lon1, this.mapCoords.lon2);
    }

  }

  private updateVisibleLibrariesList(lat1, lat2, lon1, lon2) {
    let copyOfLibraries: Library [] = [...this.librariesInView];

    this.librariesInView = [];
    for (let i = 0; i < this.libraries.length; i++) {
      if (BingMapService.isLibraryVisible(
        this.libraries[i],
        lat1,
        lat2,
        lon1,
        lon2)) {
        this.librariesInView.push(this.libraries[i]);
      }
    }
    this.pageCount = Math.ceil(this.librariesInView.length / this.pageSize);
    this.pageNumber = this.pageNumber > this.pageCount ? this.pageCount : this.pageNumber === 0 && this.pageCount > 0 ? 1 : this.pageNumber;
    this.librariesInView = [... this.librariesInView
      .slice((this.pageNumber - 1) * this.pageSize, this.pageNumber * this.pageSize)];

    if (!LibraryListComponent.isEqual(copyOfLibraries, [...this.librariesInView])) {
      this.librariesWrap.nativeElement.scrollTop = 0;
    }

  }

  sortLibs(a: Library, b: Library) {
    return /*!(a.online ===true || b.online === true) ? */this.librariesNumbers.get(b.name) - this.librariesNumbers.get(a.name)
    /*:
          a.online === true ? -10000 : 10000;*/
  }

  public static isEqual(arr1: Library[], arr2: Library[]): boolean {
    return arr1.sort((a, b) => (+a.id - +b.id)).toString() === arr2.sort((a, b) => (+a.id - +b.id)).toString();
  }

  onChange() {
    this.refreshOnMapMove = false;
    this.setPage(1);
    this.pageNumber = 1;
  }

  onPageChange(page: string) {
    this.setPage(+page);
  }

  getLibraryUrl(library: Library): string {
    if (library && library.url) {
      return HtmlUtilsService.createUrl(library.url);
    }
  }

  getDigitalCount() {
    return this.libraries != null ? this.libraries.filter(value => value.online).length : 0;
  }

  getNotDigitalCount() {
    return this.libraries != null ? this.libraries.filter(value => !value.online).length : 0;
  }

  hasNext() {
    return this.pageCount >= this.pageNumber + 1;
  }

  hasPrev() {
    return this.pageNumber - 1 >= 1;
  }

  goToNext() {
    this.pageNumber++;
    this.setPage(this.pageNumber);
  }

  goToPrev() {
    this.pageNumber--;
    this.setPage(this.pageNumber);
  }

  filterDigital(force?: boolean): Library[] {
    if (this.onlyDigital || force) {
      return this.libraries.filter(value => value.online);
    } else {
      return this.libraries;
    }
  }

  toggleList() {
    this.showSelectionLibsList = !this.showSelectionLibsList;
    //this.selectionLibsList.emit(this.showSelectionLibsList);
    this.selectionLibSync.emit(this.showSelectionLibsList);
  }

  private setPropeties(libs: Library[]) {
    this.librariesInView = libs;
    this.libraryCount.emit(this.librariesInView.length);
  }

  private setPage(page): void {
    let filterDigital = this.filterDigital();

    if (this.refreshOnMapMove) {
      filterDigital = filterDigital.filter(l =>
        BingMapService.isLibraryVisible(
          l,
          this.mapCoords.lat1,
          this.mapCoords.lat2,
          this.mapCoords.lon1,
          this.mapCoords.lon2));
    }

    if (page > this.pageCount) {
      this.pageNumber = this.pageCount;
      if(filterDigital != null)
        this.setPropeties(filterDigital.slice((this.pageCount - 1) * this.pageSize, this.pageCount * this.pageSize));
    } else if (page < 1) {
      this.pageNumber = 1;
      this.setPage(1);
    }
    else {
      if(filterDigital != null) {
        this.setPropeties(filterDigital.slice((page - 1) * this.pageSize, page * this.pageSize));
      }
    }
    this.pageCount = Math.ceil(filterDigital != null ? filterDigital.length / this.pageSize : 0);
  }

  private updateNumbers(libraries: Library[]): void {
    this.librariesNumbers = new Map();
    let i = 1;
    for (let lib of libraries) {
      if (lib.online) {
        this.librariesNumbers.set(lib.name, -1);
      } else {
        this.librariesNumbers.set(lib.name, i++);
      }
    }
  }

  getLibListLabel() {
    const libTxt = ' ' + this.translateService.translate('lib.list');
    if (this.showSelectionLibsList) {
      return this.translateService.translate('desktop.hide.list') + libTxt;
    } else {
      return this.translateService.translate('desktop.show.list') + libTxt;
    }
  }
}
